import {
  GapFillingOptions,
  RenderingSettingsContext,
  useRenderingSettings,
} from "@/components/common/rendering-settings-context";
import { CameraAnimation } from "@/components/r3f/animations/camera-animation";
import { useSmartNavigation } from "@/components/r3f/controls/smart-navigation/use-smart-navigation";
import { useDefaultCamera } from "@/components/r3f/cameras/active-camera-context";
import { PointCloudRenderer } from "@/components/r3f/renderers/pointcloud-renderer";
import { OrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { useAnimateCameraToPredefinedViews } from "@/hooks/use-animate-camera-to-predefined-views";
import { useCenterCameraOnPointCloud } from "@/hooks/use-center-camera-on-pointcloud";
import { useManageProjectionSwitch } from "@/hooks/use-manage-projection-switch";
import { useCached3DObject } from "@/object-cache";
import { selectClippingBox } from "@/store/clipping-box-selectors";
import { setClippingBox } from "@/store/clipping-box-slice";
import { selectIpxOrderMode } from "@/store/modes/ipx-order-mode-selectors";
import {
  setIpxPointCloud,
  setIpxPointCloudMetrics,
} from "@/store/modes/ipx-order-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveWizardStepIndex } from "@/store/wizard/wizard-selectors";
import { toVec4Tuple } from "@/tools/clip-scene-tool";
import {
  computeRegionFramingView,
  obbToRegion,
  RegionBox,
  regionToObb,
} from "@/tools/smart-clipping/smart-clipping";
import { useSmartClipping } from "@/tools/smart-clipping/use-smart-clipping";
import {
  BoxControls,
  ExplorationControls,
  StoreFboPassObj,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericPointCloudStream,
  isIElementGenericPointCloudStream,
} from "@faro-lotv/ielement-types";
import { PointerRotates, WalkOrbitControls } from "@faro-lotv/lotv";
import { selectIElement } from "@faro-lotv/project-source";
import { useThree } from "@react-three/fiber";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { Quaternion, Vector3 } from "three";
import { ExportModeRenderingPipeline } from "../export-mode/export-mode-rendering-pipeline";
import { useComputeValidExportModeScene } from "../export-mode/export-mode-state";
import { useIsExportableDataset } from "../export-mode/scans-export/use-prepare-scans-export-data";
import {
  BoxControlsViaRef,
  useManageClippingPlanesChange,
} from "../export-mode/use-manage-clip-planes-change";
import { IPX_MOCK_FALLBACK_TOTAL_BYTES } from "./ipx-config";
import { IPX_ORDER_STEPS, IpxOrderStepKey } from "./ipx-order-steps";
import { getElementFileSize } from "./ipx-size-estimate";
import { useIpxHiddenScans } from "./use-ipx-hidden-scans";
import { IpxScanSelectionReporter } from "./ipx-scan-selection-reporter";
import { recenterIpxViewEvent } from "./ipx-view-events";
import { useCaptureFloorSample } from "./floor-sample/use-capture-floor-sample";
import { useFloorAreaEstimation } from "./floor-area/use-floor-area-estimation";
import {
  computeFloorBounds,
  selectIsolatedFloor,
  useFloorAreaStore,
} from "./floor-area/floor-area-store";
import { useFloorColoring } from "./floor-area/use-floor-coloring";

const DEFINE_AREA_STEP_INDEX = IPX_ORDER_STEPS.findIndex(
  (step) => step.key === IpxOrderStepKey.defineArea,
);
const REVIEW_STEP_INDEX = IPX_ORDER_STEPS.findIndex(
  (step) => step.key === IpxOrderStepKey.review,
);

// Stay in the wizard even if the current selection has no valid point cloud
function noFallback(): void {}

/** @returns the scene of the IPX ordering wizard: the point cloud and the region box */
export function IpxOrderModeScene(): JSX.Element | null {
  useDefaultCamera();
  const dispatch = useAppDispatch();
  const defaultPointCloud = useComputeValidExportModeScene(noFallback);
  const { pointCloudId } = useAppSelector(selectIpxOrderMode);
  const selected = useAppSelector(selectIElement(pointCloudId));

  useEffect(() => {
    if (!pointCloudId && defaultPointCloud) {
      dispatch(setIpxPointCloud(defaultPointCloud.id));
    }
  }, [defaultPointCloud, dispatch, pointCloudId]);

  const pointCloud =
    selected && isIElementGenericPointCloudStream(selected)
      ? selected
      : defaultPointCloud;
  if (!pointCloud) return null;

  return (
    <>
      <IpxScanSelectionReporter />
      <PointCloudRegionScene key={pointCloud.id} pointCloud={pointCloud} />
    </>
  );
}

type PointCloudRegionSceneProps = {
  /** The point cloud to model */
  pointCloud: IElementGenericPointCloudStream;
};

function PointCloudRegionScene({
  pointCloud,
}: PointCloudRegionSceneProps): JSX.Element {
  const dispatch = useAppDispatch();
  const stepIndex = useAppSelector(selectActiveWizardStepIndex);
  const isEditingRegion = stepIndex === DEFINE_AREA_STEP_INDEX;
  const isReviewing = stepIndex === REVIEW_STEP_INDEX;

  const pointCloudObject = useCached3DObject(pointCloud, "IpxOrderModeScene");
  const {
    box: modelBB,
    cloudTransform,
    target: cloudCenter,
  } = useCenterCameraOnPointCloud(pointCloudObject);

  const sentScans = useAppSelector((state) => state.ipxOrderMode.sentScans);
  const scanPositions = useMemo(
    () => sentScans.map((scan) => scan.position),
    [sentScans],
  );
  const isScanSelectionAvailable = useIsExportableDataset();
  // The default box is computed once, around the initial scan selection
  const isSelectionReady = useAppSelector(
    (state) =>
      !isScanSelectionAvailable ||
      state.ipxOrderMode.totalScanCount !== undefined,
  );

  const hiddenScans = useIpxHiddenScans(pointCloudObject);
  useFloorAreaEstimation({
    pointCloud: pointCloudObject,
    hiddenScans,
    isActive: isReviewing,
  });
  const reviewedFloors = useFloorAreaStore((state) =>
    isReviewing ? state.estimate : undefined,
  );
  const isTinted = useFloorAreaStore((state) => state.isColoredByFloors);
  const isolatedFloor = useFloorAreaStore(selectIsolatedFloor);
  useFloorColoring(pointCloudObject, reviewedFloors, {
    isTinted,
    isolatedFloor,
  });
  const renderingSettings = useRenderingSettings();
  // Gap filling would close the see-through holes of the faded floors, eye-dome lighting would outline each point
  const pipelineSettings = useMemo(
    () =>
      isolatedFloor === undefined
        ? renderingSettings
        : {
            ...renderingSettings,
            gapFilling: GapFillingOptions.Disabled,
            eyeDomeOn: false,
          },
    [isolatedFloor, renderingSettings],
  );

  const [target, setTarget] = useState<Vector3 | undefined>(cloudCenter);
  const [boxPosition, setBoxPosition] = useState(() =>
    modelBB.getCenter(new Vector3()),
  );
  const [boxScale, setBoxScale] = useState(() =>
    modelBB.getSize(new Vector3()),
  );
  const [boxRotation, setBoxRotation] = useState(() => new Quaternion());

  const clippingBox = useAppSelector(selectClippingBox);
  useEffect(() => {
    if (!clippingBox) return;
    setBoxPosition(new Vector3().fromArray(clippingBox.position));
    setBoxScale(new Vector3().fromArray(clippingBox.size));
    setBoxRotation(new Quaternion().fromArray(clippingBox.rotation));
  }, [clippingBox]);
  const isPreparingBox = isEditingRegion && !clippingBox;

  const storeBox = useCallback(
    (obb: OrientedBoundingBox | undefined) => dispatch(setClippingBox(obb)),
    [dispatch],
  );

  const [boxControls, setBoxControls] = useState<BoxControlsViaRef>(null);
  const [controlsEnabled, setControlsEnabled] = useState(true);

  const storeFboRef = useRef<StoreFboPassObj>(null);

  const onInteractionStopped = useCallback(() => {
    setControlsEnabled(true);
    if (!boxControls) return;
    storeBox({
      position: boxControls.pos.toArray(),
      size: boxControls.size.toArray(),
      rotation: toVec4Tuple(boxControls.rot),
    });
  }, [boxControls, storeBox]);

  const { onClippingPlanesChanged, clippingPlanesPreview } =
    useManageClippingPlanesChange(
      modelBB,
      cloudTransform.worldMatrix,
      boxControls,
    );

  const {
    cameraAnimation,
    setCameraAnimation,
    setControlsForResetView,
    clippingBox: activeBox,
    setCameraView,
  } = useAnimateCameraToPredefinedViews(modelBB, setTarget);

  // A box drag interrupted by an animation or a step change never reports its end, don't leave the navigation disabled
  useEffect(() => {
    if (!isEditingRegion || cameraAnimation) setControlsEnabled(true);
  }, [cameraAnimation, isEditingRegion]);

  const { setControls: setControlsForCameraSwitch } = useManageProjectionSwitch(
    activeBox,
    setCameraView,
  );

  const [controls, setControlsState] = useState<WalkOrbitControls | null>(null);
  const setControls = useCallback(
    (controls: WalkOrbitControls | null) => {
      setControlsForCameraSwitch(controls);
      setControlsForResetView(controls);
      setControlsState(controls);
    },
    [setControlsForCameraSwitch, setControlsForResetView],
  );

  const [defaultRegion, setDefaultRegion] = useState<RegionBox>();
  const hiddenScanIndices = useMemo(
    () =>
      new Set(
        hiddenScans.flatMap(
          (id) => pointCloudObject.scanIdsToIndex?.[id] ?? [],
        ),
      ),
    [hiddenScans, pointCloudObject],
  );
  const aspect = useThree((state) => state.size.width / state.size.height);
  const camera = useThree((state) => state.camera);
  // Moves the camera without turning it, so the user keeps the side they look from
  const frameRegion = useCallback(
    (region: RegionBox) =>
      setCameraView(
        computeRegionFramingView(region, camera.quaternion, aspect),
      ),
    [aspect, camera, setCameraView],
  );
  const onDefaultRegionComputed = useCallback(
    (region: RegionBox) => {
      setDefaultRegion(region);
      storeBox(regionToObb(region));
      dispatch(
        setIpxPointCloudMetrics({
          size: region.box.getSize(new Vector3()).toArray(),
          totalBytes:
            getElementFileSize(pointCloud) ?? IPX_MOCK_FALLBACK_TOTAL_BYTES,
        }),
      );
      frameRegion(region);
    },
    [dispatch, frameRegion, pointCloud, storeBox],
  );
  // The box is cleared when the selection changes, so it fits the data selected now
  useSmartClipping({
    pointCloud: pointCloudObject,
    hiddenScanIndices,
    scannerPositions: scanPositions,
    fallbackBox: modelBB,
    isActive: isPreparingBox && isSelectionReady,
    onComputed: onDefaultRegionComputed,
  });
  const resetBox = useCallback(() => storeBox(undefined), [storeBox]);
  useCaptureFloorSample({
    pointCloud: pointCloudObject,
    hiddenScans,
    smartClipping: defaultRegion,
  });

  useTypedEvent(
    recenterIpxViewEvent,
    useCallback(() => {
      if (reviewedFloors && isolatedFloor !== undefined) {
        frameRegion({
          box: computeFloorBounds(reviewedFloors, isolatedFloor),
          yaw: 0,
        });
      } else {
        frameRegion(
          clippingBox ? obbToRegion(clippingBox) : { box: modelBB, yaw: 0 },
        );
      }
    }, [clippingBox, frameRegion, isolatedFloor, modelBB, reviewedFloors]),
  );

  const interactiveObjects = useMemo(
    () => [pointCloudObject],
    [pointCloudObject],
  );
  // Without the camera mode toggle in this mode, so always on
  useSmartNavigation(
    !cameraAnimation && controlsEnabled,
    controls,
    interactiveObjects,
    setTarget,
    true,
  );

  return (
    <>
      <PointCloudRenderer
        pointCloud={pointCloudObject}
        clippingPlanes={clippingBox ? clippingPlanesPreview : undefined}
        hiddenScans={hiddenScans}
      />
      <BoxControls
        ref={setBoxControls}
        position={boxPosition}
        size={boxScale}
        rotation={boxRotation}
        clipInside={false}
        enableRotateX={false}
        enableRotateY={false}
        // The ring turns the box fast, its vertical edges allow a finer rotation
        enableEdgeRotation
        isVisible={isEditingRegion && !!clippingBox}
        enabled={isEditingRegion && !cameraAnimation}
        onInteractionStarted={() => setControlsEnabled(false)}
        onInteractionStopped={onInteractionStopped}
        clippingPlanesChanged={onClippingPlanesChanged}
      />
      {!cameraAnimation && (
        <ExplorationControls
          ref={setControls}
          target={target}
          obstacles={[]}
          clickableModels={interactiveObjects}
          enableClickToFocus={controlsEnabled}
          enableDoubleClickToZoom={controlsEnabled}
          pointerRotates={PointerRotates.ViewDirection}
          enabled={controlsEnabled}
        />
      )}
      {cameraAnimation && (
        <CameraAnimation
          {...cameraAnimation}
          onAnimationFinished={() => setCameraAnimation(undefined)}
        />
      )}
      <RenderingSettingsContext.Provider value={pipelineSettings}>
        <ExportModeRenderingPipeline
          pointCloudObject={pointCloudObject}
          onAutoClipBox={storeBox}
          onResetClipBox={resetBox}
          storeFboRef={storeFboRef}
        />
      </RenderingSettingsContext.Provider>
    </>
  );
}
